import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import awinLogo from '../../logos/awin.png';
import config from '../../config';

const API_URL = config.API_URL;

/**
 * Le formulaire de connexion à Awin : un jeton, plusieurs comptes annonceurs à choisir.
 *
 * ## Pourquoi un composant à part
 *
 * Ce formulaire vivait dans `ConnectorsView`, à l'intérieur d'`App.js`, et n'était donc
 * atteignable que depuis l'écran Connecteurs. Le parcours d'installation devait pouvoir
 * l'ouvrir aussi ; le recopier aurait créé une seconde version à tenir d'accord avec la
 * première, pour un formulaire qui manipule un jeton secret et applique un quota.
 *
 * Le JSX et les gestionnaires sont repris **tels quels**, noms d'états compris : une extraction
 * qui en profite pour réécrire est une extraction dont on ne peut plus dire qu'elle n'a rien
 * changé.
 *
 * ## Ce qu'il crée
 *
 * Contrairement aux connecteurs OAuth, Awin **crée les comptes au moment de la connexion** : le
 * jeton ouvre N comptes annonceurs, l'utilisateur coche ceux qu'il veut, et les comptes de la
 * bibliothèque sont écrits dans la foulée. Celui qui l'appelle n'a donc pas d'étape de choix de
 * comptes à proposer ensuite. Le quota peut en écarter une partie : `nonRetenus` le dit.
 *
 * @param {boolean} ouvert
 * @param {() => void} onFermer
 * @param {string} token - JWT de l'utilisateur
 * @param {(info: {comptes: object[]}) => void} onConnecte - après enregistrement réussi
 */
function FormulaireAwin({ ouvert, onFermer, token, onConnecte }) {
  const [awinForm, setAwinForm] = useState({ apiToken: '' });
  const [awinTesting, setAwinTesting] = useState(false);
  const [awinTestResult, setAwinTestResult] = useState(null);
  const [awinComptes, setAwinComptes] = useState([]);
  const [awinChoisis, setAwinChoisis] = useState([]);
  const [awinConnecting, setAwinConnecting] = useState(false);

  const fermer = () => {
    setAwinTestResult(null);
    onFermer?.();
  };

  /**
   * Valide le jeton Awin et propose les comptes qu'il ouvre.
   *
   * Un jeton couvre N comptes annonceurs : on liste, l'utilisateur choisit. Les comptes déjà
   * rattachés arrivent cochés et désactivés, pour qu'il ne les coche pas en vain.
   */
  const handleAwinTest = async () => {
    if (!awinForm.apiToken.trim()) {
      setAwinTestResult({ success: false, error: 'Saisissez le jeton API Awin' });
      return;
    }

    setAwinTesting(true);
    setAwinTestResult(null);
    setAwinComptes([]);
    setAwinChoisis([]);
    try {
      const { data } = await axios.post(`${API_URL}/api/auth/awin/test`,
        { apiToken: awinForm.apiToken.trim() },
        { headers: { Authorization: `Bearer ${token}` } });

      setAwinTestResult(data);
      setAwinComptes(data.accounts || []);
      // Pré-cocher ce qui n'est pas déjà connecté : c'est presque toujours ce qu'on veut.
      setAwinChoisis((data.accounts || []).filter(c => !c.alreadyConnected).map(c => c.id));
    } catch (error) {
      setAwinTestResult({
        success: false,
        error: error.response?.data?.error || 'Impossible de joindre le serveur'
      });
    } finally {
      setAwinTesting(false);
    }
  };

  const handleAwinConnect = async () => {
    setAwinConnecting(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/auth/awin/connect`,
        { apiToken: awinForm.apiToken.trim(), accountIds: awinChoisis },
        { headers: { Authorization: `Bearer ${token}` } });

      // Le quota a pu écarter une partie des comptes : le dire plutôt que de laisser
      // l'utilisateur constater qu'il en manque.
      if (data.nonRetenus > 0) {
        alert(`${data.accounts.length} compte(s) connecté(s). ${data.nonRetenus} n'ont pas pu l'être : quota de comptes atteint.`);
      }

            setAwinForm({ apiToken: '' });
      setAwinTestResult(null);
      setAwinComptes([]);
      setAwinChoisis([]);
      onFermer?.();
      onConnecte?.({ comptes: data.accounts || [] });
    } catch (error) {
      setAwinTestResult({
        success: false,
        error: error.response?.data?.error || 'Erreur lors de la connexion'
      });
    } finally {
      setAwinConnecting(false);
    }
  };


  if (!ouvert) return null;

  return ReactDOM.createPortal(
        <div
          onClick={() => { fermer(); setAwinTestResult(null); }}
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
            alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: '20px'
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'white', borderRadius: '16px', width: '100%', maxWidth: '540px',
              maxHeight: '90vh', overflowY: 'auto', padding: '24px'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '6px' }}>
              <img src={awinLogo} alt="" style={{ width: '32px', height: '32px' }} />
              <h2 style={{ margin: 0, fontSize: '18px' }}>Connecter Awin</h2>
            </div>
            <p style={{ fontSize: '13px', color: '#6b7280', margin: '0 0 18px 0', lineHeight: 1.5 }}>
              Votre jeton ouvre <strong>tous les comptes annonceurs</strong> auxquels il donne
              accès. Testez-le, puis choisissez ceux à rattacher.
            </p>

            <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, marginBottom: '6px' }}>
              Jeton API Awin
            </label>
            <input
              type="password"
              value={awinForm.apiToken}
              onChange={(e) => { setAwinForm({ apiToken: e.target.value }); setAwinTestResult(null); setAwinComptes([]); }}
              placeholder="Jeton OAuth2 généré dans Awin"
              autoComplete="off"
              style={{
                width: '100%', boxSizing: 'border-box', padding: '10px 12px',
                border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '13px',
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace'
              }}
            />
            <p style={{ fontSize: '11px', color: '#9ca3af', margin: '6px 0 0 0' }}>
              Awin &rsaquo; Compte &rsaquo; Informations de l'API &rsaquo; Jeton OAuth2
            </p>

            {awinTestResult?.success === false && (
              <div style={{
                marginTop: '14px', padding: '10px 12px', borderRadius: '8px',
                background: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b', fontSize: '13px'
              }}>
                {awinTestResult.error}
              </div>
            )}

            {awinTestResult?.success && awinComptes.length === 0 && (
              <div style={{
                marginTop: '14px', padding: '10px 12px', borderRadius: '8px',
                background: '#fffbeb', border: '1px solid #fde68a', color: '#92400e', fontSize: '13px'
              }}>
                Ce jeton est valide mais n'ouvre aucun compte <strong>annonceur</strong>
                {awinTestResult.comptesEditeursIgnores > 0
                  ? ` (${awinTestResult.comptesEditeursIgnores} compte éditeur ignoré).`
                  : '.'}
              </div>
            )}

            {awinComptes.length > 0 && (
              <div style={{ marginTop: '16px' }}>
                <div style={{ fontSize: '13px', fontWeight: 500, marginBottom: '8px' }}>
                  Comptes à rattacher ({awinChoisis.length}/{awinComptes.length})
                </div>
                <div style={{
                  border: '1px solid #e5e7eb', borderRadius: '8px',
                  maxHeight: '220px', overflowY: 'auto'
                }}>
                  {awinComptes.map(compte => (
                    <label
                      key={compte.id}
                      style={{
                        display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px',
                        borderBottom: '1px solid #f3f4f6',
                        cursor: compte.alreadyConnected ? 'default' : 'pointer',
                        opacity: compte.alreadyConnected ? 0.55 : 1
                      }}
                    >
                      <input
                        type="checkbox"
                        disabled={compte.alreadyConnected}
                        checked={compte.alreadyConnected || awinChoisis.includes(compte.id)}
                        onChange={(e) => setAwinChoisis(actuels => (
                          e.target.checked
                            ? [...actuels, compte.id]
                            : actuels.filter(id => id !== compte.id)
                        ))}
                      />
                      <span style={{ flex: 1, fontSize: '13px' }}>{compte.name}</span>
                      <span style={{ fontSize: '11px', color: '#9ca3af' }}>
                        {compte.alreadyConnected ? 'déjà connecté' : compte.id}
                      </span>
                    </label>
                  ))}
                </div>
                {awinTestResult?.encryptionActive === false && (
                  <p style={{ fontSize: '11px', color: '#b45309', margin: '8px 0 0 0' }}>
                    Le chiffrement des secrets n'est pas configuré sur ce serveur : le jeton sera
                    stocké en clair.
                  </p>
                )}
              </div>
            )}

            <div style={{ display: 'flex', gap: '10px', marginTop: '20px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => { fermer(); setAwinTestResult(null); }}
                style={{
                  padding: '9px 16px', borderRadius: '8px', border: '1px solid #d1d5db',
                  background: 'white', fontSize: '13px', cursor: 'pointer'
                }}
              >
                Annuler
              </button>
              <button
                onClick={handleAwinTest}
                disabled={awinTesting || !awinForm.apiToken.trim()}
                style={{
                  padding: '9px 16px', borderRadius: '8px', border: '1px solid #d1d5db',
                  background: 'white', fontSize: '13px',
                  cursor: awinTesting ? 'wait' : 'pointer'
                }}
              >
                {awinTesting ? 'Test…' : 'Tester le jeton'}
              </button>
              {/* Connexion fermée tant que le test n'a rien proposé : c'est le contrat de
                  ChatGPT Ads, transposé au choix multiple. */}
              <button
                onClick={handleAwinConnect}
                disabled={awinConnecting || awinChoisis.length === 0}
                style={{
                  padding: '9px 16px', borderRadius: '8px', border: 'none',
                  background: awinChoisis.length === 0 ? '#e5e7eb' : '#111827',
                  color: awinChoisis.length === 0 ? '#9ca3af' : 'white',
                  fontSize: '13px',
                  cursor: awinChoisis.length === 0 ? 'not-allowed' : (awinConnecting ? 'wait' : 'pointer')
                }}
              >
                {awinConnecting ? 'Connexion…' : `Connecter ${awinChoisis.length || ''}`.trim()}
              </button>
            </div>
          </div>
        </div>,
    document.body
  );
}

export default FormulaireAwin;
